import styled, { keyframes, createGlobalStyle } from "styled-components";

// ─── Design Tokens ────────────────────────────────────────────────────────────
export const COLORS = {
  primary:       "#C05C00",
  primaryDark:   "#7A3300",
  primaryLight:  "#E8956D",
  primaryShadow: "rgba(192, 92, 0, 0.18)",
  accent:        "#D4973A",
  accentWarm:    "#F0C060",
  accentTint:    "rgba(212,151,58,0.15)",
  accentDark:    "#9E4C3B",
  bgWarm:        "#F9F1E4",
  bgLight:       "#F2E8D8",
  bgSection:     "#EDE3D5",
  white:         "#FFFFFF",
  pageBg:        "#FAF6EF",
  textDark:      "#2E1A0E",
  textBody:      "#4A2E1A",
  textMid:       "#9C7E66",
  textSoft:      "#C4A882",
  border:        "rgba(192,92,0,0.14)",
  borderLight:   "rgba(192,92,0,0.08)",
  success:       "#25D366",
  successDark:   "#1ebe5a",
  info:          "#3B82F6",
  warning:       "#F59E0B",
  danger:        "#EF4444",
};

// ─── Animations ───────────────────────────────────────────────────────────────
export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;
export const fadeIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
export const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50%       { transform: scale(1.12); opacity: 1; }
`;
export const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50%       { transform: translateY(-12px) rotate(1.5deg); }
`;
export const typingDot = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%            { transform: translateY(-6px); opacity: 1; }
`;
export const shimmerSlide = keyframes`
  0%   { background-position: -800px 0; }
  100% { background-position: 800px 0; }
`;
export const ringExpand = keyframes`
  0%   { transform: scale(0.8); opacity: 0.8; }
  100% { transform: scale(1.6); opacity: 0; }
`;
export const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
`;
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Global Styles ────────────────────────────────────────────────────────────
export const DietGlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

  .diet-planner * { box-sizing: border-box; -webkit-font-smoothing: antialiased; }
  .diet-planner {
    font-family: 'DM Sans', sans-serif;
    color: ${COLORS.textBody};
    background: ${COLORS.pageBg};
    scroll-behavior: smooth;
  }
  .diet-planner img { max-width: 100%; }
  .diet-planner button, .diet-planner a { -webkit-tap-highlight-color: transparent; }
  .diet-planner ::-webkit-scrollbar { width: 4px; }
  .diet-planner ::-webkit-scrollbar-track { background: transparent; }
  .diet-planner ::-webkit-scrollbar-thumb { background: ${COLORS.border}; border-radius: 4px; }
  .diet-planner *:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${COLORS.pageBg};
  font-family: 'DM Sans', sans-serif;
`;

export const Section = styled.section`
  padding: 80px 24px;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) { padding: 52px 16px; }
  @media (max-width: 480px) { padding: 40px 14px; }
`;

export const SectionWide = styled.section`
  padding: 80px 24px;
  width: 100%;
  @media (max-width: 768px) { padding: 52px 16px; }
`;

// ─── Bottom Navigation (Mobile) ───────────────────────────────────────────────
export const BottomNav = styled.nav`
  display: none;
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 200;
  background: rgba(249,241,228,0.97);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-top: 1px solid ${COLORS.border};
  padding: 8px 0 max(8px, env(safe-area-inset-bottom));
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
`;

export const BottomNavItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px 12px;
  background: none;
  border: none;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  transition: all 0.2s ease;
  .icon {
    font-size: 20px;
    line-height: 1;
    filter: ${({ $active }) => $active ? 'none' : 'grayscale(0.4) opacity(0.6)'};
    transform: ${({ $active }) => $active ? 'scale(1.15)' : 'scale(1)'};
    transition: all 0.2s ease;
  }
  .label {
    font-size: 10px;
    font-family: 'DM Sans', sans-serif;
    font-weight: ${({ $active }) => $active ? '500' : '400'};
    color: ${({ $active }) => $active ? COLORS.primary : COLORS.textMid};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    transition: color 0.2s ease;
  }
`;

export const MobileBottomPad = styled.div`
  height: 0;
  @media (max-width: 768px) { height: 80px; }
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────
export const HeroWrapper = styled.div`
  position: relative;
  min-height: 94vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(145deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 55%, #C8724A 100%);
  @media (max-width: 768px) {
    min-height: 100svh;
    align-items: flex-start;
    padding-top: 72px;
  }
`;

export const HeroDecor = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -140px; right: -140px;
    width: 640px; height: 640px;
    border-radius: 50%;
    background: rgba(212, 151, 58, 0.18);
    filter: blur(90px);
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -120px; left: -100px;
    width: 480px; height: 480px;
    border-radius: 50%;
    background: rgba(217, 123, 42, 0.10);
    filter: blur(70px);
  }
`;

export const HeroMandala = styled.div`
  position: absolute;
  right: -60px; top: 50%;
  transform: translateY(-50%);
  width: 520px; height: 520px;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Ccircle cx='100' cy='100' r='90' fill='none' stroke='%23F9F1E4' stroke-width='1'/%3E%3Ccircle cx='100' cy='100' r='70' fill='none' stroke='%23F9F1E4' stroke-width='0.8'/%3E%3Ccircle cx='100' cy='100' r='50' fill='none' stroke='%23F9F1E4' stroke-width='0.8'/%3E%3Ccircle cx='100' cy='100' r='30' fill='none' stroke='%23F9F1E4' stroke-width='0.8'/%3E%3Cpath d='M100 10 L110 90 L100 100 L90 90 Z' fill='%23F9F1E4' opacity='0.5'/%3E%3Cpath d='M190 100 L110 110 L100 100 L110 90 Z' fill='%23F9F1E4' opacity='0.5'/%3E%3Cpath d='M100 190 L90 110 L100 100 L110 110 Z' fill='%23F9F1E4' opacity='0.5'/%3E%3Cpath d='M10 100 L90 90 L100 100 L90 110 Z' fill='%23F9F1E4' opacity='0.5'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  animation: ${float} 10s ease-in-out infinite;
  @media (max-width: 1024px) { display: none; }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  animation: ${fadeUp} 0.9s ease both;
  @media (max-width: 768px) { padding: 0 16px 80px; }
`;

export const HeroEyebrow = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${COLORS.accentWarm};
  background: rgba(249, 241, 228, 0.12);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 18px;
  border-radius: 40px;
  border: 1px solid rgba(249, 241, 228, 0.22);
  margin-bottom: 24px;
  span {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${COLORS.accentWarm};
    display: inline-block;
    animation: ${pulse} 2s ease infinite;
  }
`;

export const HeroHeading = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(38px, 7vw, 82px);
  font-weight: 500;
  line-height: 1.04;
  color: ${COLORS.bgWarm};
  margin: 0 0 24px;
  max-width: 720px;
  em { font-style: italic; color: ${COLORS.accentWarm}; }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(15px, 2vw, 18px);
  line-height: 1.75;
  color: rgba(249, 241, 228, 0.78);
  max-width: 520px;
  margin: 0 0 40px;
`;

export const HeroCTARow = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    button, a { width: 100%; text-align: center; justify-content: center; }
  }
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────
export const BtnPrimary = styled.button`
  padding: 15px 34px;
  background: ${COLORS.accent};
  color: ${COLORS.textDark};
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  border: none;
  border-radius: 48px;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  &:hover { background: ${COLORS.accentDark}; color: ${COLORS.bgWarm}; transform: translateY(-2px); box-shadow: 0 14px 36px ${COLORS.primaryShadow}; }
  &:active { transform: translateY(0); }
  @media (max-width: 480px) { padding: 14px 28px; font-size: 14px; }
`;

export const BtnGhost = styled.button`
  padding: 14px 30px;
  background: transparent;
  color: ${COLORS.bgWarm};
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 400;
  border: 1px solid rgba(249, 241, 228, 0.38);
  border-radius: 48px;
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover { background: rgba(249, 241, 228, 0.1); border-color: rgba(249, 241, 228, 0.65); }
  @media (max-width: 480px) { padding: 13px 24px; font-size: 14px; }
`;

export const BtnSaffron = styled.button`
  padding: 14px 32px;
  background: ${COLORS.primary};
  color: ${COLORS.bgWarm};
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  border: none;
  border-radius: 48px;
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover { background: ${COLORS.primaryDark}; transform: translateY(-2px); box-shadow: 0 12px 32px ${COLORS.primaryShadow}; }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  @media (max-width: 480px) { padding: 13px 26px; font-size: 14px; }
`;

export const BtnOutline = styled.button`
  padding: 13px 28px;
  background: transparent;
  color: ${COLORS.primary};
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  border: 1.5px solid ${COLORS.primary};
  border-radius: 48px;
  cursor: pointer;
  transition: all 0.22s ease;
  &:hover { background: ${COLORS.primary}; color: ${COLORS.bgWarm}; }
  @media (max-width: 480px) { padding: 12px 22px; font-size: 13px; }
`;

export const BtnSmall = styled.button`
  padding: 8px 18px;
  background: ${COLORS.bgSection};
  color: ${COLORS.primary};
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${COLORS.border};
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover { background: ${COLORS.primary}; color: ${COLORS.bgWarm}; border-color: ${COLORS.primary}; }
`;

// ─── Section Labels ────────────────────────────────────────────────────────────
export const SectionEyebrow = styled.p`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${COLORS.primary};
  margin: 0 0 12px;
`;

export const SectionHeading = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(28px, 4vw, 50px);
  font-weight: 500;
  line-height: 1.12;
  color: ${COLORS.textDark};
  margin: 0 0 16px;
  em { font-style: italic; color: ${COLORS.primary}; }
`;

export const SectionSubtext = styled.p`
  font-size: 16px;
  line-height: 1.75;
  color: ${COLORS.textMid};
  max-width: 560px;
  margin: 0 0 48px;
  @media (max-width: 768px) { font-size: 15px; margin-bottom: 36px; }
`;

// ─── Filter Tabs ────────────────────────────────────────────────────────────
export const TabRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 40px;
  @media (max-width: 600px) { gap: 6px; }
`;

export const Tab = styled.button`
  padding: 9px 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.22s ease;
  border: 1px solid ${({ $active }) => $active ? COLORS.primary : COLORS.border};
  background: ${({ $active }) => $active ? COLORS.primary : COLORS.white};
  color: ${({ $active }) => $active ? COLORS.bgWarm : COLORS.textBody};
  &:hover { border-color: ${COLORS.primary}; color: ${({ $active }) => $active ? COLORS.bgWarm : COLORS.primary}; }
  @media (max-width: 480px) { padding: 8px 16px; font-size: 12px; }
`;

// ─── Info Cards ───────────────────────────────────────────────────────────────
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  @media (max-width: 480px) { grid-template-columns: 1fr; gap: 16px; }
`;

export const InfoCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 20px;
  padding: 28px;
  transition: all 0.28s ease;
  animation: ${fadeUp} 0.6s ease both;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  &:hover { transform: translateY(-4px); box-shadow: 0 20px 48px ${COLORS.primaryShadow}; border-color: ${COLORS.primaryLight}; }
  @media (max-width: 480px) { padding: 22px 18px; }
`;

export const CardIcon = styled.div`
  width: 52px; height: 52px;
  border-radius: 14px;
  background: ${({ $bg }) => $bg || COLORS.bgLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 18px;
`;

export const CardTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 22px;
  font-weight: 500;
  color: ${COLORS.textDark};
  margin: 0 0 10px;
`;

export const CardText = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: ${COLORS.textMid};
  margin: 0 0 16px;
`;

export const CardTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: ${COLORS.bgSection};
  color: ${COLORS.primary};
  font-size: 11px;
  font-weight: 500;
  border-radius: 20px;
  margin: 3px 3px 0 0;
  letter-spacing: 0.04em;
`;

// ─── Chat ─────────────────────────────────────────────────────────────────────
export const ChatSection = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  overflow: hidden;
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px ${COLORS.primaryShadow};
`;

export const ChatHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const ChatAvatar = styled.div`
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(249,241,228,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

export const ChatHeaderInfo = styled.div`
  h4 { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 500; color: ${COLORS.bgWarm}; margin: 0 0 3px; }
  p  { font-size: 12px; color: rgba(249,241,228,0.65); margin: 0; }
`;

export const ChatBody = styled.div`
  padding: 24px;
  min-height: 320px;
  max-height: 440px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;
`;

export const BotBubble = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  animation: ${fadeUp} 0.35s ease both;
`;

export const BotMsg = styled.div`
  background: ${COLORS.bgSection};
  border-radius: 4px 18px 18px 18px;
  padding: 14px 18px;
  font-size: 14px;
  line-height: 1.65;
  color: ${COLORS.textDark};
  max-width: 80%;
  white-space: pre-wrap;
`;

export const UserBubble = styled.div`
  display: flex;
  justify-content: flex-end;
  animation: ${slideInRight} 0.3s ease both;
`;

export const UserMsg = styled.div`
  background: ${COLORS.primary};
  color: ${COLORS.bgWarm};
  border-radius: 18px 4px 18px 18px;
  padding: 12px 18px;
  font-size: 14px;
  line-height: 1.6;
  max-width: 75%;
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${COLORS.bgSection};
  border-radius: 4px 18px 18px 18px;
  padding: 14px 18px;
  span {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: ${COLORS.textMid};
    animation: ${typingDot} 1.2s ease infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

export const OptionsRow = styled.div`
  padding: 0 24px 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const OptionChip = styled.button`
  padding: 9px 18px;
  background: ${({ $selected }) => $selected ? COLORS.primary : COLORS.white};
  color: ${({ $selected }) => $selected ? COLORS.bgWarm : COLORS.textBody};
  border: 1.5px solid ${({ $selected }) => $selected ? COLORS.primary : COLORS.border};
  border-radius: 40px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${COLORS.primary}; color: ${COLORS.primary}; }
`;

export const ChatInput = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid ${COLORS.borderLight};
  background: ${COLORS.bgWarm};
`;

export const ChatTextInput = styled.input`
  flex: 1;
  padding: 12px 18px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 40px;
  color: ${COLORS.textDark};
  outline: none;
  transition: border-color 0.2s ease;
  &::placeholder { color: ${COLORS.textMid}; }
  &:focus { border-color: ${COLORS.primary}; }
`;

export const ChatSendBtn = styled.button`
  width: 44px; height: 44px;
  background: ${COLORS.primary};
  color: ${COLORS.bgWarm};
  border: none;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover { background: ${COLORS.primaryDark}; transform: scale(1.05); }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;

// ─── Progress Bar ──────────────────────────────────────────────────────────────
export const ProgressBar = styled.div`
  height: 4px;
  background: rgba(249,241,228,0.25);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 16px;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct || 0}%;
  background: ${COLORS.accentWarm};
  border-radius: 2px;
  transition: width 0.5s ease;
`;

// ─── Dashboard ─────────────────────────────────────────────────────────────────
export const DashboardWrapper = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 8px 40px ${COLORS.primaryShadow};
`;

export const DashboardHeader = styled.div`
  padding: 28px 32px;
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  h3 { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 500; color: ${COLORS.bgWarm}; margin: 0 0 4px; }
  p  { font-size: 13px; color: rgba(249,241,228,0.7); margin: 0; }
`;

export const DashUserInfo = styled.div``;

export const DashBadge = styled.div`
  background: rgba(249,241,228,0.18);
  color: ${COLORS.bgWarm};
  border: 1px solid rgba(249,241,228,0.3);
  border-radius: 40px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
`;

export const DashBody = styled.div`
  padding: 32px;
  @media (max-width: 768px) { padding: 20px 16px; }
`;

export const DashGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

export const StatCard = styled.div`
  background: ${({ $bg }) => $bg || COLORS.bgWarm};
  border-radius: 16px;
  padding: 18px 20px;
`;

export const StatLabel = styled.p`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.textMid};
  margin: 0 0 8px;
`;

export const StatValue = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 32px;
  font-weight: 500;
  color: ${({ $color }) => $color || COLORS.textDark};
  margin: 0;
  line-height: 1;
`;

export const StatUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${COLORS.textMid};
  margin-left: 4px;
`;

// ─── Meal Plan ─────────────────────────────────────────────────────────────────
export const MealPlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

export const MealCard = styled.div`
  background: ${COLORS.bgWarm};
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${COLORS.borderLight};
  transition: all 0.25s ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 12px 32px ${COLORS.primaryShadow}; }
`;

export const MealCardTime = styled.div`
  background: ${COLORS.primaryDark};
  color: ${COLORS.bgWarm};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 8px 14px;
`;

export const MealCardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MealName = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 17px;
  font-weight: 500;
  color: ${COLORS.textDark};
  margin: 0;
  line-height: 1.3;
`;

export const MealMeta = styled.p`
  font-size: 12px;
  color: ${COLORS.textMid};
  margin: 0;
`;

export const NutrientRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

export const NutrientPill = styled.span`
  padding: 3px 9px;
  background: rgba(192,92,0,0.1);
  color: ${COLORS.primary};
  border-radius: 20px;
  font-size: 10px;
  font-weight: 500;
`;

// ─── Recipe Card ─────────────────────────────────────────────────────────────
export const RecipeCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.28s ease;
  animation: ${fadeUp} 0.6s ease both;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  &:hover { transform: translateY(-4px); box-shadow: 0 20px 48px ${COLORS.primaryShadow}; }
`;

export const RecipeImage = styled.div`
  height: 160px;
  background: ${({ $bg }) => $bg || COLORS.bgLight};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const RecipeBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(46,26,14,0.75);
  color: ${COLORS.bgWarm};
  font-size: 10px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 20px;
  letter-spacing: 0.05em;
`;

export const RecipeBody = styled.div`
  padding: 18px;
`;

export const RecipeTitle = styled.h4`
  font-family: 'Cormorant Garamond', serif;
  font-size: 19px;
  font-weight: 500;
  color: ${COLORS.textDark};
  margin: 0 0 10px;
  line-height: 1.3;
`;

export const RecipeMeta = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: ${COLORS.textMid};
  margin-bottom: 16px;
`;

// ─── Consult Banner ───────────────────────────────────────────────────────────
export const ConsultBanner = styled.div`
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  border-radius: 28px;
  padding: 48px;
  text-align: center;
  @media (max-width: 768px) { padding: 32px 20px; }
`;

export const ConsultHeading = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(26px, 4vw, 38px);
  font-weight: 500;
  color: ${COLORS.bgWarm};
  margin: 0 0 12px;
`;

export const ConsultText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: rgba(249,241,228,0.75);
  max-width: 480px;
  margin: 0 auto 32px;
`;

export const ConsultBtnRow = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

export const WABtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: ${COLORS.success};
  color: ${COLORS.white};
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  border-radius: 48px;
  text-decoration: none;
  transition: all 0.25s ease;
  &:hover { background: ${COLORS.successDark}; transform: translateY(-2px); }
`;

// ─── Water Tracker ─────────────────────────────────────────────────────────────
export const WaterTracker = styled.div`
  background: linear-gradient(135deg, #EDF5FF, #DAF0FF);
  border-radius: 20px;
  padding: 20px 24px;
  border: 1px solid rgba(59,130,246,0.15);
`;

export const WaterGlasses = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

export const WaterGlass = styled.button`
  width: 38px; height: 38px;
  font-size: 20px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.15s ease;
  padding: 0;
  &:hover { transform: scale(1.2); }
`;

// ─── Food Lists ─────────────────────────────────────────────────────────────────
export const FoodListGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const FoodList = styled.div`
  background: ${({ $variant }) => $variant === "avoid" ? "#FFF5F5" : "#F0FDF4"};
  border: 1px solid ${({ $variant }) => $variant === "avoid" ? "rgba(239,68,68,0.15)" : "rgba(22,163,74,0.15)"};
  border-radius: 20px;
  padding: 24px;
`;

export const FoodListTitle = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  font-weight: 500;
  color: ${({ $variant }) => $variant === "avoid" ? "#B91C1C" : "#15803D"};
  margin: 0 0 16px;
`;

export const FoodItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  font-size: 14px;
  color: ${COLORS.textBody};
  &:last-child { border-bottom: none; }
`;

// ─── Highlight Strip ──────────────────────────────────────────────────────────
export const HighlightStrip = styled.div`
  background: ${COLORS.primaryDark};
  padding: 24px;
  display: flex;
  justify-content: center;
  gap: 48px;
  flex-wrap: wrap;
  @media (max-width: 640px) { gap: 24px; }
`;

export const StripStat = styled.div`
  text-align: center;
  strong {
    display: block;
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 500;
    color: ${COLORS.accentWarm};
  }
  span {
    font-size: 12px;
    color: rgba(249,241,228,0.65);
    letter-spacing: 0.05em;
  }
`;

export const OmDivider = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  color: rgba(249,241,228,0.35);
  text-align: center;
  letter-spacing: 0.3em;
`;

// ─── Forms ─────────────────────────────────────────────────────────────────────
export const FormCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  padding: 36px;
  box-shadow: 0 4px 24px ${COLORS.primaryShadow};
  @media (max-width: 768px) { padding: 24px 18px; }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 2}, 1fr);
  gap: 20px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.textMid};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const FormInput = styled.input`
  padding: 12px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  background: ${COLORS.bgWarm};
  border: 1.5px solid ${COLORS.border};
  border-radius: 12px;
  color: ${COLORS.textDark};
  outline: none;
  transition: all 0.2s ease;
  &::placeholder { color: ${COLORS.textSoft}; }
  &:focus { border-color: ${COLORS.primary}; background: ${COLORS.white}; box-shadow: 0 0 0 3px rgba(192,92,0,0.08); }
`;

export const FormSelect = styled.select`
  padding: 12px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  background: ${COLORS.bgWarm};
  border: 1.5px solid ${COLORS.border};
  border-radius: 12px;
  color: ${COLORS.textDark};
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
  &:focus { border-color: ${COLORS.primary}; }
`;

export const FormTextarea = styled.textarea`
  padding: 12px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  background: ${COLORS.bgWarm};
  border: 1.5px solid ${COLORS.border};
  border-radius: 12px;
  color: ${COLORS.textDark};
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  &::placeholder { color: ${COLORS.textSoft}; }
  &:focus { border-color: ${COLORS.primary}; background: ${COLORS.white}; box-shadow: 0 0 0 3px rgba(192,92,0,0.08); }
`;

export const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
`;

export const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ $checked }) => $checked ? "rgba(192,92,0,0.08)" : COLORS.bgWarm};
  border: 1.5px solid ${({ $checked }) => $checked ? COLORS.primary : COLORS.border};
  border-radius: 12px;
  cursor: pointer;
  font-size: 13px;
  color: ${({ $checked }) => $checked ? COLORS.primary : COLORS.textBody};
  font-weight: ${({ $checked }) => $checked ? '500' : '400'};
  transition: all 0.2s ease;
  &:hover { border-color: ${COLORS.primary}; }
  input[type="checkbox"] { accent-color: ${COLORS.primary}; width: 16px; height: 16px; }
`;

// ─── Modal ─────────────────────────────────────────────────────────────────────
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(46,26,14,0.55);
  backdrop-filter: blur(8px);
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalBox = styled.div`
  background: ${COLORS.white};
  border-radius: 28px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${fadeUp} 0.3s ease;
  position: relative;
  @media (max-width: 480px) { padding: 28px 20px; border-radius: 20px; }
`;

export const ModalClose = styled.button`
  position: absolute;
  top: 20px; right: 20px;
  width: 36px; height: 36px;
  background: ${COLORS.bgSection};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover { background: ${COLORS.bgLight}; }
`;

// ─── Tracker ───────────────────────────────────────────────────────────────────
export const TrackerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const TrackerCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  padding: 28px;
`;

export const WeightChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 120px;
  padding: 8px 0;
`;

export const WeightBar = styled.div`
  flex: 1;
  background: ${({ $active }) => $active ? COLORS.primary : COLORS.bgSection};
  border-radius: 6px 6px 0 0;
  height: ${({ $h }) => $h || '40%'};
  transition: all 0.5s ease;
  cursor: pointer;
  position: relative;
  &:hover::after {
    content: attr(data-label);
    position: absolute;
    bottom: 105%;
    left: 50%;
    transform: translateX(-50%);
    background: ${COLORS.textDark};
    color: ${COLORS.bgWarm};
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 11px;
    white-space: pre;
    z-index: 10;
  }
`;

// ─── Condition Cards ───────────────────────────────────────────────────────────
export const ConditionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  @media (max-width: 480px) { grid-template-columns: repeat(2, 1fr); gap: 12px; }
`;

export const ConditionCard = styled.div`
  background: ${COLORS.white};
  border: 2px solid ${({ $active }) => $active ? COLORS.primary : COLORS.border};
  border-radius: 20px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  background: ${({ $active }) => $active ? "rgba(192,92,0,0.05)" : COLORS.white};
  &:hover { border-color: ${COLORS.primary}; transform: translateY(-3px); box-shadow: 0 12px 32px ${COLORS.primaryShadow}; }
`;

export const ConditionIcon = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
`;

export const ConditionName = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 16px;
  font-weight: 500;
  color: ${({ $active }) => $active ? COLORS.primary : COLORS.textDark};
  margin: 0 0 6px;
`;

export const ConditionTag = styled.p`
  font-size: 11px;
  color: ${({ $active }) => $active ? COLORS.primary : COLORS.textMid};
  margin: 0;
`;

// ─── AI Plan ─────────────────────────────────────────────────────────────────
export const AIPlanCard = styled.div`
  border: 1px solid ${COLORS.border};
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 12px 48px ${COLORS.primaryShadow};
`;

export const AIPlanHeader = styled.div`
  padding: 28px 32px;
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
`;

export const AIPlanBody = styled.div`
  padding: 32px;
  background: ${COLORS.white};
  @media (max-width: 768px) { padding: 20px 16px; }
`;

export const MacroRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 28px;
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

export const MacroCell = styled.div`
  background: ${({ $bg }) => $bg || COLORS.bgWarm};
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  strong { display: block; font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 500; color: ${({ $color }) => $color || COLORS.textDark}; }
  span   { font-size: 12px; color: ${COLORS.textMid}; margin-top: 4px; display: block; }
`;

export const LoadingSpinner = styled.div`
  width: 40px; height: 40px;
  border: 3px solid ${COLORS.bgSection};
  border-top-color: ${COLORS.primary};
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: ${spin} 0.9s linear infinite;
`;

// ─── Yoga Recommendations ─────────────────────────────────────────────────────
export const YogaRecoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
`;

export const YogaRecoCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.25s ease;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  &:hover { transform: translateY(-4px); box-shadow: 0 16px 40px ${COLORS.primaryShadow}; }
`;

export const YogaRecoImg = styled.div`
  height: 140px;
  background: ${({ $bg }) => $bg || COLORS.bgLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
`;

export const YogaRecoBody = styled.div`
  padding: 20px;
`;

export const YogaRecoTitle = styled.h4`
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-weight: 500;
  color: ${COLORS.textDark};
  margin: 0 0 6px;
`;

export const YogaRecoMeta = styled.p`
  font-size: 13px;
  color: ${COLORS.textMid};
  margin: 0 0 16px;
  line-height: 1.5;
`;

// ─── Admin ─────────────────────────────────────────────────────────────────────
export const AdminLayout = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  min-height: 70vh;
  gap: 0;
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  overflow: hidden;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const AdminSidebar = styled.div`
  background: ${COLORS.primaryDark};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 768px) { display: none; }
`;

export const AdminSidebarItem = styled.button`
  padding: 12px 16px;
  background: ${({ $active }) => $active ? "rgba(249,241,228,0.15)" : "transparent"};
  color: ${({ $active }) => $active ? COLORS.bgWarm : "rgba(249,241,228,0.6)"};
  border: none;
  border-radius: 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '500' : '400'};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  &:hover { background: rgba(249,241,228,0.1); color: ${COLORS.bgWarm}; }
`;

export const AdminContent = styled.div`
  padding: 32px;
  background: ${COLORS.bgWarm};
  @media (max-width: 768px) { padding: 20px 16px; }
`;

export const AdminTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    padding: 12px 16px;
    background: ${COLORS.bgSection};
    color: ${COLORS.textMid};
    font-weight: 500;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    &:first-child { border-radius: 10px 0 0 10px; }
    &:last-child  { border-radius: 0 10px 10px 0; }
  }
  td {
    padding: 14px 16px;
    border-bottom: 1px solid ${COLORS.borderLight};
    color: ${COLORS.textBody};
    vertical-align: middle;
  }
  tr:last-child td { border-bottom: none; }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  background: ${({ $color }) => $color || COLORS.bgSection};
  color: ${({ $textColor }) => $textColor || COLORS.primary};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
`;

export const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

export const AnalyticsCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 18px;
  padding: 22px;
`;

// ─── Lead Banner ──────────────────────────────────────────────────────────────
export const LeadBanner = styled.div`
  background: linear-gradient(135deg, #FFF8ED, #F5E8CC);
  border: 1px solid rgba(192,92,0,0.15);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  @media (max-width: 768px) { padding: 28px 20px; }
`;

export const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, ${COLORS.accent}, ${COLORS.primary});
  color: ${COLORS.bgWarm};
  border-radius: 40px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 16px;
`;

// ─── Free Chat / Mode Toggle ─────────────────────────────────────────────────
export const FreeChatWrapper = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

export const ChatModeToggle = styled.div`
  display: flex;
  gap: 4px;
  background: ${COLORS.bgSection};
  border-radius: 40px;
  padding: 4px;
  margin-bottom: 24px;
  width: fit-content;
`;

export const ChatModeBtn = styled.button`
  padding: 10px 22px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  border-radius: 36px;
  border: none;
  cursor: pointer;
  transition: all 0.22s ease;
  background: ${({ $active }) => $active ? COLORS.primary : "transparent"};
  color: ${({ $active }) => $active ? COLORS.bgWarm : COLORS.textMid};
`;

export const ChatQuickActions = styled.div`
  padding: 12px 18px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid ${COLORS.borderLight};
  background: ${COLORS.bgWarm};
`;

export const QuickChip = styled.button`
  padding: 7px 14px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 40px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: ${COLORS.textBody};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover { border-color: ${COLORS.primary}; color: ${COLORS.primary}; background: rgba(192,92,0,0.04); }
`;

// ─── New: BMI / Calorie Result Card ──────────────────────────────────────────
export const ResultCard = styled.div`
  background: ${({ $bg }) => $bg || COLORS.bgWarm};
  border: 1px solid ${COLORS.border};
  border-radius: 20px;
  padding: 24px;
  text-align: center;
`;

// ─── New: Chat History Item ─────────────────────────────────────────────────
export const HistoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${COLORS.borderLight};
  font-size: 13px;
  color: ${COLORS.textBody};
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: ${COLORS.bgWarm}; }
  &:last-child { border-bottom: none; }
  .preview { color: ${COLORS.textMid}; font-size: 12px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 260px; }
  .time { color: ${COLORS.textSoft}; font-size: 11px; flex-shrink: 0; }
`;

// ─── New: Nutrient Bar ──────────────────────────────────────────────────────
export const NutrientBar = styled.div`
  margin-bottom: 12px;
  .label { display: flex; justify-content: space-between; font-size: 13px; color: ${COLORS.textBody}; margin-bottom: 6px; }
  .track { height: 8px; background: ${COLORS.bgSection}; border-radius: 4px; overflow: hidden; }
  .fill  { height: 100%; width: ${({ $pct }) => $pct || 0}%; background: ${({ $color }) => $color || COLORS.primary}; border-radius: 4px; transition: width 0.6s ease; }
`;

// ─── New: Profile Completion Ring Wrapper ───────────────────────────────────
export const ProfileRingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: ${COLORS.bgWarm};
  border-radius: 18px;
  padding: 20px;
  margin-bottom: 24px;
  .desc { font-size: 14px; color: ${COLORS.textBody}; line-height: 1.6; }
  .desc strong { color: ${COLORS.primary}; }
`;




